/**
 * AutomationListItem — shared list item for displaying a single CRM automation.
 *
 * Used by both the Automations and Websites settings sections. Renders a
 * `ButtonList.Item`: the whole row opens the automation; the enable/disable
 * toggle sits in the item's `trailing` slot, which isolates its clicks from
 * the row action.
 */

import { useCallback } from 'react';

import { ButtonList } from '@/lib/base/button-list';
import { AutomationEnabledToggle } from '@/lib/crm/automations/AutomationEnabledToggle';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';

export interface AutomationListItemProps {
  automation: CrmObject;
  summarySchema: PropertyTypeSchema | undefined;
  orgId: number;
  onToggle: (
    entityId: string,
    enabled: string,
    automationName?: string
  ) => void;
  onRowClick: (entityId: string) => void;
  isToggling?: boolean;
  canToggle?: boolean;
}

export function AutomationListItem({
  automation,
  summarySchema,
  orgId,
  onToggle,
  onRowClick,
  isToggling,
  canToggle = false,
}: AutomationListItemProps) {
  const name =
    (automation.properties?.['ahoy_display_name']?.value as
      | string
      | undefined) ?? 'Untitled automation';
  const enabled =
    (automation.properties?.['ahoy_enabled']?.value as string | undefined) ===
    'true';
  const summaryValue = automation.properties?.['ahoy_summary']?.value;
  const automationName = automation.properties?.['ahoy_automation_name']
    ?.value as string | undefined;

  const handleAction = useCallback(() => {
    onRowClick(automation.entity_id);
  }, [automation.entity_id, onRowClick]);

  const handleToggleClick = useCallback(
    (isSelected: boolean) => {
      onToggle(
        automation.entity_id,
        isSelected ? 'true' : 'false',
        automationName
      );
    },
    [automation.entity_id, automationName, onToggle]
  );

  // Shared with the record slideout's sub-header, so the permission-gated
  // tooltip treatment cannot drift between the two surfaces.
  const toggle = (
    <AutomationEnabledToggle
      enabled={enabled}
      canToggle={canToggle}
      isBusy={isToggling}
      onChange={handleToggleClick}
    />
  );

  return (
    <ButtonList.Item
      onAction={handleAction}
      aria-label={`Open ${name} details`}
      trailing={toggle}
      showChevron
    >
      <span className='text-sm font-medium text-fg-primary'>{name}</span>
      {summarySchema && summaryValue != null ? (
        <div className='mt-0.5'>
          <PropertyValueLabel
            value={summaryValue}
            propertyType={summarySchema}
            orgId={orgId}
            fontSize='xs'
          />
        </div>
      ) : (
        <p className='mt-0.5 text-xs text-fg-tertiary'>
          {summaryValue ? String(summaryValue) : ''}
        </p>
      )}
    </ButtonList.Item>
  );
}
